/**
 * 템플릿 목차 트리·합본 순서 — FE 미리보기와 BE SubMatMergePdfService 가 동일 규칙을 쓴다.
 */
import { compareGeneralFolderNodes } from '../../store/subMatFolder';

/** Word식 목차 번호: 1/1.1/1.1.1 | I/A/1/a | 1./1)/(1) | 기호 */
export const TOC_NUMBERING = {
  DECIMAL_HIERARCHY: 'DECIMAL_HIERARCHY',
  OUTLINE: 'OUTLINE',
  SIBLING: 'SIBLING',
  BULLET: 'BULLET',
};

export const TOC_NUMBERING_OPTIONS = [
  {
    value: TOC_NUMBERING.DECIMAL_HIERARCHY,
    label: '계층 번호 (1, 1.1, 1.1.1)',
    sample: '1 일반문서\n  1.1 서류명\n    1.1.1 하위',
  },
  {
    value: TOC_NUMBERING.OUTLINE,
    label: '개요 번호 (I, A, 1, a)',
    sample: 'I. 일반문서\n  A. 서류명\n    1. 하위',
  },
  {
    value: TOC_NUMBERING.SIBLING,
    label: '단계 번호 (1. / 1) / (1))',
    sample: '1. 일반문서\n  1) 서류명\n    (1) 하위',
  },
  {
    value: TOC_NUMBERING.BULLET,
    label: '글머리 기호 (■ / • / –)',
    sample: '■ 일반문서\n  • 서류명\n    – 하위',
  },
];

export const normalizeTocNumbering = (value) => {
  if (
    value === TOC_NUMBERING.OUTLINE
    || value === TOC_NUMBERING.SIBLING
    || value === TOC_NUMBERING.BULLET
    || value === TOC_NUMBERING.DECIMAL_HIERARCHY
  ) {
    return value;
  }
  return TOC_NUMBERING.DECIMAL_HIERARCHY;
};

/** null·미지정이면 true(기존 A4+헤더+푸터) */
export const normalizeBodyHeaderPaging = (value) => {
  if (value === false || value === 'false') return false;
  return true;
};

/** 본문 페이지 서식 — UI 토글 값 (DB bodyHeaderPaging 과 1:1) */
export const BODY_PAGE_FORMAT = {
  A4_HEADER_PAGING: 'A4_HEADER_PAGING',
  ORIGINAL_APPEND: 'ORIGINAL_APPEND',
};

export const BODY_PAGE_FORMAT_OPTIONS = [
  {
    value: BODY_PAGE_FORMAT.A4_HEADER_PAGING,
    label: '보임',
    description: 'A4 세로에 머리글·페이지번호를 넣고, 가로 원본은 세워 배치합니다.',
  },
  {
    value: BODY_PAGE_FORMAT.ORIGINAL_APPEND,
    label: '안보임',
    description: '첨부 PDF의 페이지 크기와 내용을 그대로 이어 붙입니다.',
  },
];

export const bodyHeaderPagingToPageFormat = (bodyHeaderPaging) => (
  normalizeBodyHeaderPaging(bodyHeaderPaging)
    ? BODY_PAGE_FORMAT.A4_HEADER_PAGING
    : BODY_PAGE_FORMAT.ORIGINAL_APPEND
);

export const pageFormatToBodyHeaderPaging = (format) => (
  format !== BODY_PAGE_FORMAT.ORIGINAL_APPEND
);

export const COMPARISON_TOC_TITLE = '시험성과대비표';

export const comparisonSlotSourceKey = (folderId) => `folder:${folderId}:comparison`;

export const isTestComparisonMasterRoot = (item) => (
  (item?.rootKind === 'TEST' || item?.rootKind === 'ETC')
  && (item?.isSystem === true || item?.folderLevel === 0)
);

export const itemHasTestComparison = (item) => item?.hasTestComparison === true;

export const shouldIncludeComparisonInToc = (item) => (
  item?.includeTestComparison === true && itemHasTestComparison(item)
);

export const collectDescendantComparisonIds = (itemId, childrenMap) => {
  const ids = [];
  const walk = (id) => {
    (childrenMap?.[id] || []).forEach((k) => {
      if (itemHasTestComparison(k)) ids.push(k.id);
      walk(k.id);
    });
  };
  walk(itemId);
  return ids;
};

export const shouldShowTestComparisonCheckbox = (item, _childrenMap) => {
  if (isTestComparisonMasterRoot(item)) return true;
  return itemHasTestComparison(item);
};

export const tocDisplayTitle = (item) => {
  if (item?.titleOverride?.trim()) return item.titleOverride.trim();
  return item?.folderName || item?.folderId || '';
};

/** 폴더 없는 목차 제목. 표시 이름은 titleOverride. */
export const isHeadingItem = (item) => Boolean(item) && !item.folderId;

/** 제목 아래에 서류 폴더가 하나라도 있는지. 포함 여부와 무관. */
export const headingHasDocuments = (item, childrenMap) => {
  const walk = (id) => (childrenMap?.[id] || []).some((kid) => (
    !isHeadingItem(kid) || walk(kid.id)
  ));
  return Boolean(item?.id) && walk(item.id);
};

const headingHasIncludedDocuments = (item, childrenMap) => {
  const walk = (id) => (childrenMap?.[id] || []).some((kid) => {
    if (kid.included === false) return false;
    if (!isHeadingItem(kid)) return true;
    return walk(kid.id);
  });
  return Boolean(item?.id) && walk(item.id);
};

/** 합본 목차에 나오는 항목. 서류가 없는 제목은 번호에서도 뺀다. */
export const isVisibleTocItem = (item, childrenMap) => {
  if (!item || item.included === false) return false;
  if (!isHeadingItem(item)) return true;
  return headingHasIncludedDocuments(item, childrenMap);
};

/** 형제 sortOrder → 요청 배열 index tie-break */
export const buildChildrenMap = (items) => {
  const sourceIndex = {};
  (items || []).forEach((it, i) => {
    if (it?.id != null) sourceIndex[it.id] = i;
  });
  const map = {};
  (items || []).forEach((it) => {
    const key = it.parentItemId || '';
    if (!map[key]) map[key] = [];
    map[key].push(it);
  });
  Object.keys(map).forEach((k) => {
    map[k].sort((a, b) => {
      const d = (a.sortOrder || 0) - (b.sortOrder || 0);
      if (d !== 0) return d;
      return (sourceIndex[a.id] ?? 0) - (sourceIndex[b.id] ?? 0);
    });
  });
  return map;
};

/** parentItemId 가 folderId 로 들어온 경우 item id 로 치환. 유효한 id 는 그대로 둔다. */
export const resolveTemplateItemParentIds = (items) => {
  const list = (items || []).map((it) => ({ ...it }));
  const itemIds = new Set(list.map((it) => it.id).filter(Boolean));
  const folderIdToItemId = {};
  list.forEach((it) => {
    if (it.folderId) folderIdToItemId[it.folderId] = it.id;
  });
  return list.map((it) => {
    let parentItemId = it.parentItemId || null;
    if (parentItemId && !itemIds.has(parentItemId)) {
      const mapped = folderIdToItemId[parentItemId];
      parentItemId = mapped && mapped !== it.id ? mapped : null;
    }
    return { ...it, parentItemId };
  });
};

/**
 * 일반문서 형제만 폴더 탐색기와 같은 순서로 맞춘다.
 * 서류 종류가 있으면 등록 순서, 없으면 그 아래 가나다. 제목이 섞인 묶음은 그대로 둔다.
 */
export const orderGeneralTocSiblings = (items, folders, generalDocOrder) => {
  if (!items?.length || !generalDocOrder?.size) return items;
  const folderById = {};
  (folders || []).forEach((folder) => {
    if (folder?.id) folderById[folder.id] = folder;
  });
  const next = items.map((it) => ({ ...it }));
  const byId = {};
  next.forEach((it) => { byId[it.id] = it; });
  const groups = {};
  next.forEach((it) => {
    const key = it.parentItemId || '';
    if (!groups[key]) groups[key] = [];
    groups[key].push(it);
  });
  let changed = false;
  Object.values(groups).forEach((siblings) => {
    const metas = siblings.map((it) => (it.folderId ? folderById[it.folderId] : null));
    if (metas.some((folder) => !folder)) return;
    if (!metas.every((folder) => folder.rootKind === 'GENERAL' && folder.parentId)) return;
    const sorted = [...siblings].sort((a, b) => compareGeneralFolderNodes(
      folderById[a.folderId],
      folderById[b.folderId],
      generalDocOrder,
    ));
    sorted.forEach((it, index) => {
      const sortOrder = index + 1;
      if (byId[it.id].sortOrder !== sortOrder) {
        byId[it.id].sortOrder = sortOrder;
        changed = true;
      }
    });
  });
  return changed ? next : items;
};

/** buildItems 결과를 탐색기 순서로 다시 늘어 놓는다. 이어서 추가할 때도 형제 순서가 유지된다. */
export const orderBuiltItemsLikeExplorer = (built, folders, generalDocOrder) => {
  const adjusted = orderGeneralTocSiblings(built, folders, generalDocOrder);
  if (!adjusted?.length) return built || [];
  const byParent = {};
  adjusted.forEach((it) => {
    const key = it.parentItemId || '';
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(it);
  });
  Object.values(byParent).forEach((list) => {
    list.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  });
  const out = [];
  const walk = (parentId) => {
    (byParent[parentId || ''] || []).forEach((it) => {
      out.push(it);
      walk(it.id);
    });
  };
  walk('');
  return out.length === adjusted.length ? out : adjusted;
};

/** 편집 로드 시 깨진 parentItemId 는 폴더 등록 트리로 추론 (드래그 결과는 유지) */
export const repairTemplateItemLinksForDisplay = (items, folders) => {
  const resolved = resolveTemplateItemParentIds(items);
  const ids = new Set(resolved.map((it) => it.id).filter(Boolean));
  const folderIdToItemId = {};
  resolved.forEach((it) => {
    if (it.folderId) folderIdToItemId[it.folderId] = it.id;
  });
  const folderById = {};
  (folders || []).forEach((f) => {
    folderById[f.id] = f;
  });
  return resolved.map((it) => {
    if (it.parentItemId && ids.has(it.parentItemId)) return it;
    const folder = it.folderId ? folderById[it.folderId] : null;
    const inferred = folder?.parentId ? folderIdToItemId[folder.parentId] : null;
    const parentItemId = inferred && inferred !== it.id ? inferred : null;
    return { ...it, parentItemId };
  });
};

const toRoman = (n) => {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I'];
  let num = n;
  let out = '';
  for (let i = 0; i < vals.length; i += 1) {
    while (num >= vals[i]) {
      out += syms[i];
      num -= vals[i];
    }
  }
  return out || String(n);
};

const toAlphaUpper = (n) => {
  let num = n;
  let out = '';
  while (num > 0) {
    num -= 1;
    out = String.fromCharCode(65 + (num % 26)) + out;
    num = Math.floor(num / 26);
  }
  return out || 'A';
};

const toAlphaLower = (n) => toAlphaUpper(n).toLowerCase();

/** @param {number[]} path 루트부터 현재까지 1-based 형제 인덱스 */
export const formatTocLabel = (depth, path, title, style) => {
  const numbering = normalizeTocNumbering(style);
  const index = path[path.length - 1] || 1;
  const safeTitle = title || '';

  if (numbering === TOC_NUMBERING.DECIMAL_HIERARCHY) {
    return `${path.join('.')} ${safeTitle}`;
  }
  if (numbering === TOC_NUMBERING.OUTLINE) {
    if (depth <= 0) return `${toRoman(index)}. ${safeTitle}`;
    if (depth === 1) return `${toAlphaUpper(index)}. ${safeTitle}`;
    if (depth === 2) return `${index}. ${safeTitle}`;
    if (depth === 3) return `${toAlphaLower(index)}. ${safeTitle}`;
    return `${index}) ${safeTitle}`;
  }
  if (numbering === TOC_NUMBERING.BULLET) {
    if (depth <= 0) return `■ ${safeTitle}`;
    if (depth === 1) return `• ${safeTitle}`;
    return `– ${safeTitle}`;
  }
  // SIBLING
  if (depth <= 0) return `${index}. ${safeTitle}`;
  if (depth === 1) return `${index}) ${safeTitle}`;
  if (depth === 2) return `(${index}) ${safeTitle}`;
  return `- ${safeTitle}`;
};

const pushComparisonEntry = (entries, depth, parentPath, style) => {
  const path = [...parentPath, 1];
  entries.push({
    depth: depth + 1,
    text: formatTocLabel(depth + 1, path, COMPARISON_TOC_TITLE, style),
    sectionGap: false,
    showPageHint: true,
  });
};

const walkIncludedEntries = (parentId, depth, parentPath, childrenMap, entries, style, startIndexOffset = 0) => {
  const kids = (childrenMap[parentId] || []).filter((it) => isVisibleTocItem(it, childrenMap));
  kids.forEach((kid, i) => {
    const path = [...parentPath, i + 1 + startIndexOffset];
    entries.push({
      depth,
      text: formatTocLabel(depth, path, tocDisplayTitle(kid), style),
      sectionGap: false,
      // PDF 합본과 동일: 현재 문서 있는 항목만 페이지 힌트
      showPageHint: kid.hasCurrentFile === true,
    });
    const comparisonChild = shouldIncludeComparisonInToc(kid);
    if (comparisonChild) {
      pushComparisonEntry(entries, depth, path, style);
    }
    walkIncludedEntries(
      kid.id,
      depth + 1,
      path,
      childrenMap,
      entries,
      style,
      comparisonChild ? 1 : 0,
    );
  });
};

/**
 * 목차 미리보기 구조화 줄 (depth·라벨·페이지힌트). PDF 배율 렌더용.
 * @returns {{ depth: number, text: string, sectionGap: boolean, showPageHint?: boolean }[]}
 */
export const buildMergedTocPreviewEntries = (items, tocNumbering, extraAttachments, extraSectionTitle) => {
  const style = normalizeTocNumbering(tocNumbering);
  const childrenMap = buildChildrenMap(items);
  const roots = (childrenMap[''] || []).filter((it) => isVisibleTocItem(it, childrenMap));
  const extras = (extraAttachments || []).filter((e) => e && e.included !== false);
  const sectionTitle = (extraSectionTitle && String(extraSectionTitle).trim()) || '별도 첨부';

  if (!roots.length && !extras.length) return [];

  const entries = [];
  roots.forEach((root, rootIdx) => {
    if (rootIdx > 0) entries.push({ depth: 0, text: '', sectionGap: true });
    const path = [rootIdx + 1];
    entries.push({
      depth: 0,
      text: formatTocLabel(0, path, tocDisplayTitle(root), style),
      sectionGap: false,
      showPageHint: root.hasCurrentFile === true,
    });
    const comparisonChild = shouldIncludeComparisonInToc(root);
    if (comparisonChild) {
      pushComparisonEntry(entries, 0, path, style);
    }
    walkIncludedEntries(
      root.id,
      1,
      path,
      childrenMap,
      entries,
      style,
      comparisonChild ? 1 : 0,
    );
  });

  if (extras.length) {
    if (entries.length) entries.push({ depth: 0, text: '', sectionGap: true });
    const rootIndex = roots.length + 1;
    entries.push({
      depth: 0,
      text: formatTocLabel(0, [rootIndex], sectionTitle, style),
      sectionGap: false,
      showPageHint: false,
    });
    extras.forEach((ex, i) => {
      const title = (ex.title || ex.file?.name || `첨부${i + 1}`).replace(/\.pdf$/i, '');
      entries.push({
        depth: 1,
        text: formatTocLabel(1, [rootIndex, i + 1], title, style),
        sectionGap: false,
        showPageHint: true,
      });
    });
  }
  return entries;
};

/** @returns {string[]} 목차 미리보기 줄 (DFS pre-order) */
export const buildMergedTocPreviewLines = (items, tocNumbering, extraAttachments, extraSectionTitle) => (
  buildMergedTocPreviewEntries(items, tocNumbering, extraAttachments, extraSectionTitle).map((e) => {
    if (e.sectionGap) return '';
    const indent = e.depth <= 0 ? '' : '  '.repeat(e.depth);
    return `${indent}${e.text}`;
  })
);

/** @returns {SubMatTemplateItem[]} 목차 DFS pre-order (미리보기·합본 공통) */
export const walkIncludedItemsInOrder = (items) => {
  const childrenMap = buildChildrenMap(items);
  const ordered = [];
  const walk = (parentId) => {
    (childrenMap[parentId] || [])
      .filter((it) => it.included !== false)
      .forEach((it) => {
        ordered.push(it);
        walk(it.id);
      });
  };
  walk('');
  return ordered;
};

/** previewMerge API body.items — parentItemId 정규화 */
export const buildMergePreviewItemsPayload = (items) => (
  resolveTemplateItemParentIds(items).map((it) => ({
    id: it.id,
    folderId: it.folderId || null,
    parentItemId: it.parentItemId || null,
    titleOverride: it.titleOverride?.trim() || null,
    sortOrder: it.sortOrder ?? 0,
    included: it.included !== false,
    includeTestComparison: it.includeTestComparison === true,
    folderName: it.folderName || null,
  }))
);

/** previewMerge API body — 미리보기와 동일 DFS 순서를 folderOrder 로 명시 */
export const buildMergePreviewPayload = (items, name, tocNumbering, bodyHeaderPaging = true) => ({
  name: name || '',
  tocNumbering: normalizeTocNumbering(tocNumbering),
  bodyHeaderPaging: normalizeBodyHeaderPaging(bodyHeaderPaging),
  items: buildMergePreviewItemsPayload(items),
  folderOrder: walkIncludedItemsInOrder(items).map((it) => it.folderId).filter(Boolean),
});
